<template>
  <v2-card no-padding>
    <template #header>
      <div class="v2-profile-team__header">
        <h3 class="v2-heading-sm">
          Team Members
        </h3>
        <v2-button
          v-if="!isSalesRep"
          variant="primary"
          size="sm"
          @click="onAddTeammate"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle
              cx="8.5"
              cy="7"
              r="4"
            />
            <line
              x1="20"
              y1="8"
              x2="20"
              y2="14"
            />
            <line
              x1="23"
              y1="11"
              x2="17"
              y2="11"
            />
          </svg>
          Add Teammate
        </v2-button>
      </div>
    </template>

    <!-- Loading state -->
    <template v-if="isLoadingTeammates">
      <div class="v2-profile-team__loading">
        <v2-spinner
          size="lg"
          label="Loading team members..."
        />
      </div>
    </template>

    <!-- Empty state -->
    <template v-else-if="!teamMatesList.length">
      <div class="v2-profile-team__empty">
        <span class="v2-text-sm v2-text-muted">No team members found.</span>
      </div>
    </template>

    <!-- Table -->
    <template v-else>
      <v2-table
        :columns="teamColumns"
        :items="paginatedTeammates"
      >
        <template #cell(firstName)="{ item }">
          <span class="v2-font-medium">{{ item.firstName }}</span>
        </template>
        <template #cell(lastName)="{ item }">
          <span class="v2-font-medium">{{ item.lastName }}</span>
        </template>
        <template #cell(email)="{ item }">
          {{ item.email }}
        </template>
        <template #cell(role)="{ item }">
          <span class="v2-profile-team__role">{{ USER_ROLES_LABEL[item.role] || item.role }}</span>
        </template>
        <template #cell(status)="{ item }">
          <v2-badge :variant="parseStatusVariant(item.status)">
            {{ item.status }}
          </v2-badge>
        </template>
        <template #cell(_id)="{ item }">
          <div class="v2-profile-team__actions">
            <button
              class="v2-profile-team__icon-btn"
              title="Edit"
              @click="onEditTeammate(item)"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              class="v2-profile-team__icon-btn v2-profile-team__icon-btn--destructive"
              title="Delete"
              @click="onDeleteTeammate(item)"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        </template>
      </v2-table>

      <v2-pagination
        v-if="hasPagination"
        :current-page="currentPage"
        :total-items="totalAdditionalUsers"
        :per-page="perPage"
        @page-change="currentPage = $event"
      />
    </template>

    <!-- Invite/Edit Modal -->
    <v2-invite-teammate-modal
      :show="showInviteModal"
      :edit-data="editTeammateData"
      :roles="teamMatesRoles"
      :sales-managers="salesManagers"
      :is-entity-admin="isEntityAdmin"
      :is-saving="isSaving"
      @close="onCloseInviteModal"
      @invite="onInviteTeammate"
      @update="onUpdateTeammate"
    />

    <!-- Delete Confirmation Modal -->
    <v2-delete-teammate-modal
      :show="showDeleteModal"
      :teammate="selectedAdditionalUserForDelete"
      @close="showDeleteModal = false"
      @confirm="onTeammateDeleted"
    />
  </v2-card>
</template>

<script>
import { ref } from '@vue/composition-api'
import { useManageTeam } from '@/composables/useManageTeam'
import V2Card from '@/components/v2/V2Card.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Table from '@/components/v2/V2Table.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Pagination from '@/components/v2/V2Pagination.vue'
import V2InviteTeammateModal from '@/views/v2/manage-team/V2InviteTeammateModal.vue'
import V2DeleteTeammateModal from '@/views/v2/manage-team/V2DeleteTeammateModal.vue'

export default {
  name: 'V2ProfileTeammates',
  components: {
    V2Card,
    V2Button,
    V2Table,
    V2Badge,
    V2Spinner,
    V2Pagination,
    V2InviteTeammateModal,
    V2DeleteTeammateModal,
  },
  props: {
    additionalUsers: {
      type: Array,
      default: () => [],
    },
    getUserProfile: {
      type: Function,
      default: null,
    },
  },
  setup() {
    const team = useManageTeam()
    team.loadAllScopes()

    const showInviteModal = ref(false)
    const showDeleteModal = ref(false)
    const editTeammateData = ref(null)

    // Simplified columns (retailer — exclude notification bell)
    const teamColumns = [
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'role', label: 'Role' },
      { key: 'status', label: 'Status', sortable: true },
      { key: '_id', label: 'Actions' },
    ]

    return {
      ...team,
      showInviteModal,
      showDeleteModal,
      editTeammateData,
      teamColumns,
    }
  },
  methods: {
    onAddTeammate() {
      this.editTeammateData = null
      this.clearFormData()
      this.showInviteModal = true
    },
    onEditTeammate(item) {
      this.editTeammateData = { ...item }
      this.populateFormForEdit(item)
      this.showInviteModal = true
    },
    onCloseInviteModal() {
      this.showInviteModal = false
      this.editTeammateData = null
      this.clearFormData()
    },
    async onInviteTeammate(payload) {
      const success = await this.sendInvite(payload)
      if (success) this.onCloseInviteModal()
    },
    async onUpdateTeammate(payload) {
      const success = await this.updateUser(payload)
      if (success) this.onCloseInviteModal()
    },
    onDeleteTeammate(item) {
      this.selectedAdditionalUserForDelete = item
      this.showDeleteModal = true
    },
    async onTeammateDeleted() {
      const success = await this.deleteUser()
      if (success) {
        this.showDeleteModal = false
        if (this.getUserProfile) {
          this.getUserProfile()
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-profile-team {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: var(--v2-space-4) var(--v2-space-6);
  }

  &__loading,
  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    padding: var(--v2-space-6);
  }

  &__role {
    text-transform: capitalize;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-foreground);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--v2-muted);
    }

    &--destructive {
      color: var(--v2-destructive);

      &:hover {
        background: rgba(229, 72, 77, 0.1);
      }
    }
  }

  @media (max-width: 640px) {
    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--v2-space-3);
    }
  }
}
</style>
